// Core
import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  Button,
  Box,
  Flex,
  Text,
  useColorModeValue,
  useColorMode,
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';
import { validateLoadMore, parseDateTime } from 'services/Utils';
import { ReactComponent as TransactionIcon } from 'assets/svg/transaction-history-2.svg';
import { ReactComponent as EmptyListNotifications } from 'assets/svg/empty-list-notifications.svg';

// Components
import Card from 'components/Card/Card';
import Errors from 'components/Form/Errors';
import Success from 'components/Form/Success';

const TransactionHistory = ({ userData }) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const bg = useColorModeValue('white', '#0E1633');
  const border = useColorModeValue('1px solid #CBE7FF', '1px solid #262D47');
  const boxShadow = useColorModeValue('unset', '0px 1px 3px 0px rgba(0, 0, 0, 0.10),0px 1px 2px 0px rgba(0, 0, 0, 0.06)');

  const limit = 24;
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState([]);
  const [dataMeta, setDataMeta] = useState();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const queryClient = useQueryClient();
  const { isLoading } = useQuery({
      queryKey: ['transaction-history', offset],
      queryFn: async () => {
        if(!localStorage.getItem('auth')) { return true; }

        const response = await apiService.getTransactionHistory({ limit, offset });
        if(offset === 0){
            setData(response.data);
        } else {
            setData([...data, ...response.data]);
        }
        setDataMeta(response.meta);
        return true;
      },
      refetchOnWindowFocus: false
  });

  const loadMore = async () => {
      setOffset(offset + limit);
  };

  const handleSyncPreviousPurchases = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      const response = await apiService.syncPreviousPurchases();
      const synced = response?.data?.synced || 0;
      setSyncResult(synced > 0 ? `${synced} previous purchases loaded` : 'No new purchases found');
      if (synced > 0) {
        setOffset(0);
        setData([]);
        queryClient.invalidateQueries('transaction-history');
      }
    } catch (err) {
      setSyncError(err?.message || 'Failed to load previous purchases. Please try again later');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
  	<>
      { syncError && <Errors error={syncError} mb="16px" /> }
      { syncResult && <Success success={syncResult} mb="16px" /> }

      <Flex justifyContent="center" mb="16px">
        <Button
          variant="secondary-md"
          maxW="280px"
          onClick={handleSyncPreviousPurchases}
          isLoading={isSyncing}
          isDisabled={isSyncing}
        >
          Load Previous Purchases
        </Button>
      </Flex>

    	{ data.length > 0 ? (
		    <Card padding="0px 0 12px 12px" bg={bg} border={border} boxShadow={boxShadow} borderRadius="12px">	    
			    { data.map((item, index) => (
			        <Flex key={index} p="12px 0 0 0" gap="20px" alignItems="center" w="100%">
			          <Box p="4px" w="32px" h="32px" borderRadius="8px" bg="rgba(9, 155, 255, 0.10)" position="relative">
			            <TransactionIcon fontSize="24px" />
			          </Box>

			          <Flex borderBottom={ isDark ? '1px solid #262D47' : '1px solid #CBE7FF'} pb="10px" w="100%" justifyContent="space-between">
			          	<Box>
				            <Text 
				            	variant="title-md" 
				            	fontWeight="700" 
				            	textTransform="capitalize"
				            	mb="3px">
				            	{item?.title ?? `${item?.type === 'invoiceitem' ? 'Invoice' : item?.type ?? '' } payment`}
				            </Text>
				            <Text
				              variant="text-sm"
				              w="100%"
				              textTransform="capitalize"
				              overflow="hidden" 
				              display="-webkit-box" 
				              style={{
				                WebkitLineClamp: 2, 
				                WebkitBoxOrient: 'vertical',
				                textOverflow: 'ellipsis'
				              }}
				            >
				              {item?.description ? item?.description : item?.planName ? item?.planName : `Invoice: ${item?.invoiceId}`}
				            </Text>
				            <Text mt="3px" variant="text-xs" color={ isDark ? '#9FABCB' : '#262D47' }>{parseDateTime(item?.createdAt)}</Text>
			            </Box>

			            { item?.amount !== null && 
				            <Text 
				            	variant="title-md"
				            	color={ item?.amount < 0 ? '#FF6C64' : item?.amount > 0 ? '#57D175' : 'unset' } 
				            	pr="8px">${item?.amount ? item?.amount / 100 : 0}</Text>
				          }
			          </Flex>


			        </Flex>
			    ))}

			    { validateLoadMore(data, dataMeta) && (
			        <Button isLoading={isLoading} variant="ghost-md" maxW="220px" onClick={loadMore} m="24px auto !important">
			          Load More
			        </Button>
			    )}
		    </Card>
	    ) : (
	    	<Box 
              textAlign="center" 
              py="40px" 
              maxW={{sm: '325px'}} 
              left="50%" 
              position="relative" 
              transform="translate(-50%, 0)">
                <EmptyListNotifications style={{margin: '0 auto'}} />
                <Text variant="title-md" fontSize="20px" mt="20px" pb="9px" letterSpacing="-0.4px">No Notifications</Text>
                <Text>We’ll let you know when there will be something to update you</Text>
            </Box>
	    )}

    </>
  );
};

export default TransactionHistory;